import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Button,
  Box,
  TextField,
  Tooltip,
  Link as MuiLink,
  CircularProgress,
  Alert
} from '@mui/material';

import CloseIcon        from '@mui/icons-material/Close';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import OpenInNewIcon    from '@mui/icons-material/OpenInNew';
import DownloadIcon     from '@mui/icons-material/Download';

import CreateLink  from './CreateLink';
import CreatePdf   from './CreatePdf';

function ShareResultsDialog({ open, onClose, userData, analysisData }) {
  const [format, setFormat] = useState('private-link');
  const [isDownloading, setIsDownloading] = useState(false);

  /* Check availability of analysis data */
  const hasAnalysis = useMemo(() => {
    return analysisData && (
      (analysisData.surveyAnalysis && Object.keys(analysisData.surveyAnalysis).length > 0) || 
      (analysisData.questionAnalysis && Object.keys(analysisData.questionAnalysis).length > 0)
    );
  }, [analysisData]);

  const [includeSurvey, setIncludeSurvey] = useState(true);
  const [includePersonas, setIncludePersonas] = useState(true);
  const [includeResponses, setIncludeResponses] = useState(true);
  const [includeAnalysis, setIncludeAnalysis] = useState(false);

  useEffect(() => {
    if (hasAnalysis) setIncludeAnalysis(true);
    else setIncludeAnalysis(false);
  }, [hasAnalysis]);

  const hasSelection = includeSurvey || includePersonas || includeResponses || includeAnalysis;

  const dataToExport = useMemo(() => {
    return {
      ...userData,
      includeSurvey,
      includePersonas,
      includeResponses,
      includeAnalysis, // <--- FIXED: Explicitly passing the boolean flag
      analysis: includeAnalysis ? analysisData : null 
    };
  }, [userData, analysisData, includeSurvey, includePersonas, includeResponses, includeAnalysis]);

  /* Success Dialog State */
  const [successOpen, setSuccessOpen] = useState(false);
  const [successType, setSuccessType] = useState('link');
  const [successData, setSuccessData] = useState('');

  const openSuccess = (payload, type = 'link') => {
    setSuccessType(type);
    setSuccessData(payload);
    onClose();
    setSuccessOpen(true);
    setIsDownloading(false);
  };

  /* Render Action Button (Main Dialog) */
  const renderActionButton = () => {
    switch (format) {
      case 'pdf':
        return (
          <CreatePdf
            userData={dataToExport} 
            disabled={!hasSelection} 
            onSuccess={(url) => openSuccess(url, 'pdf')} 
          />
        );
      default: 
        // For private link, we don't enforce selection rules (it shares the project)
        return (
          <CreateLink
            userData={dataToExport} 
            disabled={false}
            onSuccess={(url) => openSuccess(url, 'link')}
          />
        );
    }
  };

  /* Force Download Helper */
  const handleForceDownload = async () => {
    if (!successData) return;
    setIsDownloading(true);
    try {
      const response = await fetch(successData);
      const blob = await response.blob();
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Modified: Use userData.title directly as requested
      const fileName = userData?.title || 'research';
      link.download = `${fileName}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(successData, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  /* Render Success Body (Preview Dialog) */
  const renderSuccessBody = () => {
    if (successType === 'pdf') {
       return (
        <>
          <Box
            sx={{
              width: '100%',
              height: 420,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <object
              data={successData}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6">PDF Preview</Typography>
                <Typography variant="body2">{userData?.title}</Typography>
              </Box>
            </object>
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={isDownloading}
            startIcon={isDownloading ? <CircularProgress size={20} color="inherit"/> : <DownloadIcon />}
            onClick={handleForceDownload}
          >
            {isDownloading ? 'Downloading...' : 'Download .pdf'}
          </Button>
        </>
      );
    }
    
    // Default Link view
    return (
      <>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField fullWidth size="small" value={successData} InputProps={{ readOnly: true }} />
          <Tooltip title="Copy to clipboard">
            <IconButton onClick={() => navigator.clipboard.writeText(successData)} edge="end">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <OpenInNewIcon fontSize="small" color="action" />
          <MuiLink href={successData} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2">
            Open
          </MuiLink>
        </Box>
      </>
    );
  };
  
  const successTitle = successType === 'pdf' ? 'PDF Preview' : 'Success! Your link is ready.';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="share-results-dialog-title"
      >
        <DialogTitle id="share-results-dialog-title" sx={{ pr: 5 }}>
          {`Share "${userData?.title || ''}"`}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Choose how you would like to share your research results.
          </Typography>

          {/* 1. Format Selection moved to top */}
          <FormLabel component="legend" sx={{ mb: 1 }}>Format</FormLabel>
          <RadioGroup 
            value={format} 
            onChange={(e) => setFormat(e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel value="private-link" control={<Radio />} label="Private Link" />
            <FormControlLabel value="pdf"          control={<Radio />} label="PDF Document" />
          </RadioGroup>

          {/* NEW: Informational Alert for Private Link */}
          {format === 'private-link' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This creates a static snapshot of your current results. Future
              updates will require a new link.&nbsp;
              You can manage or delete active links in{' '}
              <MuiLink
                href="/account"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
              
              My&nbsp;Account
                <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.25 }} />
              </MuiLink>.
            </Alert>
          )}

          {/* 2. Selection Options (Only visible for PDF) */}
          {format === 'pdf' && (
            <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Include in PDF:
              </Typography>
              
              {!hasSelection && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    * Select at least one section below
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel 
                  control={<Checkbox size="small" checked={includeSurvey} onChange={(e) => setIncludeSurvey(e.target.checked)} />} 
                  label="Survey & prompt" 
                />
                <FormControlLabel 
                  control={<Checkbox size="small" checked={includePersonas} onChange={(e) => setIncludePersonas(e.target.checked)} />} 
                  label="Personas" 
                />
                <FormControlLabel 
                  control={<Checkbox size="small" checked={includeResponses} onChange={(e) => setIncludeResponses(e.target.checked)} />} 
                  label="Responses" 
                />
                <FormControlLabel 
                  control={<Checkbox size="small" checked={includeAnalysis} onChange={(e) => setIncludeAnalysis(e.target.checked)} disabled={!hasAnalysis} />} 
                  label={hasAnalysis ? "Analysis report" : "Analysis report (Not generated yet)"}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          {renderActionButton()}
        </DialogActions>
      </Dialog>

      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {successTitle}
          <IconButton
            aria-label="close"
            onClick={() => setSuccessOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderSuccessBody()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ShareResultsDialog;